import { useEffect, useId, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { useLanguage } from "../../i18n/language-provider";
import { activatePmsConnection, fetchPmsConnections } from "../../services/pmsConnectionsApi";
import { runWhenUnblocked } from "../../navigation/history";
import { activeConnectionChangedCopy } from "./ActiveConnectionChangedNotice";
import { getConnectionLabel } from "./connectionLabel";

interface PropertyOpenedElsewhereNoticeProps {
    /** The property the tab was loaded for — the one the sidebar names. */
    loadedConnectionId: number;
    /** The property open now, as the service named it; null with none open. */
    openConnectionId: number | null | undefined;
    /**
     * The page under the notice holds unsaved work, kept hidden rather than
     * emptied: opening the tab's property again is then the way to save it.
     */
    holdsUnsavedWork: boolean;
    /** The tab's own property is open again: the shell asks, and the page comes back. */
    onReopened: () => void;
    /** How the page is reloaded; a seam for tests. */
    reload?: () => void;
}

/** The two properties by name, once the list is read; null until then or when it cannot be. */
type Names = { loaded: string | null; open: string | null } | null;

function reloadPage(): void {
    window.location.reload();
}

/**
 * The notice as it is drawn: from its words, from what the shell hands it and
 * from the names it is given, and from nothing it reads itself. Both exports
 * below are this one, with the names and without them, so the two cannot say
 * different things.
 */
function DrawnNotice({
    names,
    loadedConnectionId,
    openConnectionId,
    holdsUnsavedWork,
    onReopened,
    reload = reloadPage,
}: PropertyOpenedElsewhereNoticeProps & { names: Names }) {
    const { t } = useLanguage();
    const copy = activeConnectionChangedCopy(t);
    const descriptionId = useId();
    const [reopening, setReopening] = useState(false);

    const noneOpen = openConnectionId === null;
    const loadedName = names?.loaded ?? null;
    const openName = noneOpen ? copy.noneOpen : names?.open ?? null;

    const reopen = async () => {
        setReopening(true);
        try {
            await activatePmsConnection(loadedConnectionId);
            onReopened();
        } catch {
            toast.error(copy.switchBackFailed);
        } finally {
            setReopening(false);
        }
    };

    // One primary action: the way that keeps the work when there is work to
    // keep, the reload otherwise.
    const reloadButton = (
        <Button
            type="button"
            size="sm"
            variant={holdsUnsavedWork ? "outline" : "default"}
            onClick={() => runWhenUnblocked(reload)}
            disabled={reopening}
            aria-describedby={descriptionId}
            className="h-10 sm:h-8"
        >
            {copy.reload}
        </Button>
    );
    const reopenButton = (
        <Button
            type="button"
            size="sm"
            variant={holdsUnsavedWork ? "default" : "outline"}
            onClick={() => void reopen()}
            disabled={reopening}
            className="h-10 sm:h-8"
        >
            {loadedName ? copy.switchBack.replace("{name}", loadedName) : copy.switchBackUnnamed}
        </Button>
    );

    return (
        <Alert variant="destructive">
            <AlertTriangle aria-hidden="true" />
            <AlertTitle>{noneOpen ? copy.closedTitle : copy.openedTitle}</AlertTitle>
            <AlertDescription>
                {/* Sized on the line itself: the base layer sizes a bare <p>
                    at 18px whatever its container says. */}
                <p id={descriptionId} className="text-sm">
                    {noneOpen ? copy.closedDescription : copy.openedDescription}
                </p>
                {loadedName && openName && (
                    <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm">
                        <dt>{copy.thisTab}</dt>
                        <dd className="min-w-0 font-medium break-words">{loadedName}</dd>
                        <dt>{copy.openNow}</dt>
                        <dd className="min-w-0 font-medium break-words">{openName}</dd>
                    </dl>
                )}
                {holdsUnsavedWork && (
                    <p className="text-sm">
                        {loadedName ? copy.unsavedKept.replace("{name}", loadedName) : copy.unsavedKeptUnnamed}
                    </p>
                )}
                <div className="mt-1 flex flex-wrap gap-2">
                    {holdsUnsavedWork ? (
                        <>
                            {reopenButton}
                            {reloadButton}
                        </>
                    ) : (
                        <>
                            {reloadButton}
                            {reopenButton}
                        </>
                    )}
                </div>
            </AlertDescription>
        </Alert>
    );
}

/**
 * Said in place of the page when the tab shows a property that is no longer
 * the one open.
 *
 * The open property is server-side state every tab, window and device of a
 * user shares, and a page reads it once. A tab left behind after another one
 * opened a second property showed the first one's data beside the second's —
 * the strategy editor with no room type left (30 Sep 2026) — and wrote to the
 * second under the first one's name. The shell finds out (`App.tsx`) and shows
 * this instead: which property the tab shows and which one is open now, and
 * the two ways on. Reloading shows the open property, and goes through the
 * discard guard, so unsaved work is asked about first. Opening the tab's own
 * property again hands the page back as it was — the way to save work kept
 * under the notice.
 *
 * It names the two properties from a list it reads itself, the list the rail's
 * switcher reads. The names are a courtesy: until the list arrives, and
 * whenever it cannot be read, the notice says what happened without them. A
 * list that is no list is caught where it is read, here, and costs the names
 * alone. A name that cannot be drawn is thrown where it is drawn, so the shell
 * draws the notice inside a boundary, with `PropertyOpenedElsewhereNoticeUnnamed`
 * to stand in.
 */
export function PropertyOpenedElsewhereNotice(props: PropertyOpenedElsewhereNoticeProps) {
    const { loadedConnectionId, openConnectionId } = props;
    const { t } = useLanguage();
    const [names, setNames] = useState<Names>(null);

    useEffect(() => {
        let cancelled = false;
        fetchPmsConnections()
            .then((connections) => {
                if (cancelled) return;
                const nameOf = (id: number | null | undefined) => {
                    const found = id == null ? undefined : connections.find((conn) => conn.id === id);
                    return found ? getConnectionLabel(found, t) : null;
                };
                setNames({ loaded: nameOf(loadedConnectionId), open: nameOf(openConnectionId) });
            })
            .catch(() => {
                // The names are a courtesy: the notice says what happened without them.
            });
        return () => {
            cancelled = true;
        };
    }, [loadedConnectionId, openConnectionId, t]);

    return <DrawnNotice {...props} names={names} />;
}

/**
 * The notice without the properties' names: how it reads whenever its list
 * cannot be read, and so what stands in its place when the names it did read
 * cannot be drawn (`App.tsx` draws the notice inside a boundary).
 *
 * It reads no list, so it cannot fail the way the notice did. Everything else
 * the notice is for stays: it says what happened, and it keeps both ways on.
 * The reload goes through the discard guard as the notice's own does. Opening
 * the tab's property again needs the property's id and not its name, and is
 * how work kept under the notice is saved.
 */
export function PropertyOpenedElsewhereNoticeUnnamed(props: PropertyOpenedElsewhereNoticeProps) {
    return <DrawnNotice {...props} names={null} />;
}
